import { useEffect, useMemo } from "react";

import { useMe } from "@/hooks/api/useMe";
import { useAuthStore } from "@/store/authStore";
import type { User } from "@/types";

export function useAuth() {
  const session = useAuthStore((s) => s.session);
  const isLoadingAuth = useAuthStore((s) => s.isLoadingAuth);
  const meQuery = useMe({ enabled: !!session });

  const meMatchesSession = !!session && meQuery.data?.userId === session.id;
  const meForSession = meMatchesSession ? meQuery.data : undefined;
  const isMeMismatch = !!session && !!meQuery.data && meQuery.data.userId !== session.id;
  const isMeLoading =
    !!session && !meForSession && (meQuery.isPending || meQuery.isFetching || isMeMismatch);

  const refetchMe = meQuery.refetch;
  useEffect(() => {
    if (isMeMismatch) {
      void refetchMe();
    }
  }, [isMeMismatch, refetchMe]);

  const user = useMemo((): User | null => {
    if (!session || isMeLoading || !meForSession) return null;
    return {
      id: session.id,
      email: session.email,
      displayName: meForSession.displayName ?? null,
      birthDate: meForSession.birthDate ?? null,
      preferredLanguage: meForSession.preferredLanguage ?? null,
      avatarUrl: meForSession.avatarUrl ?? null,
      canOnboard: meForSession.canOnboard ?? false,
      needsPlatformInviteAccept: meForSession.needsPlatformInviteAccept ?? false,
      needsWorkerOnboarding: meForSession.needsWorkerOnboarding ?? false,
      createWorkerOnAccept: meForSession.createWorkerOnAccept ?? false,
      platformInviteStatus: meForSession.platformInviteStatus ?? null,
      subscriptionIntent: meForSession.subscriptionIntent ?? null,
      accountOrigin: meForSession.accountOrigin ?? null,
      accessLock: meForSession.accessLock ?? null,
      memberStatus: meForSession.memberStatus ?? null,
      isEnabled: meForSession.isEnabled ?? null,
      organizations: meForSession.organizations ?? [],
      organization: meForSession.organization ?? null,
      subscription: meForSession.subscription ?? null,
      capabilities: meForSession.capabilities ?? null,
      permissions: meForSession.permissions ?? [],
      permissionTemplate: meForSession.permissionTemplate ?? null,
      isOrgAdmin: meForSession.isOrgAdmin ?? false,
      isOwner: meForSession.isOwner ?? false,
      isPlatformStaff: meForSession.isPlatformStaff ?? false,
      staffImpersonation: meForSession.staffImpersonation ?? null,
    };
  }, [session, isMeLoading, meForSession]);

  const isError = !!session && meQuery.isError && !meForSession && !isMeMismatch;

  return {
    user,
    session,
    isLoading: isLoadingAuth || isMeLoading,
    isError,
    refetch: () => meQuery.refetch(),
  };
}
